import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ApiResponse, useMemoizedArrayData } from 'src/hooks/api/api';
import {
  Funnel,
  FunnelListResponse,
  CrmFunnelsService,
  FunnelListQueryParams,
} from 'src/api/generated';

interface UseFunnelsParams extends FunnelListQueryParams {
  enabled?: boolean;
}

interface UseFunnelsResponse extends ApiResponse {
  funnels: Funnel[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

export const useFunnels = (
  params: UseFunnelsParams = {}
): UseFunnelsResponse => {
  const { enabled = true, ...queryParams } = params;

  const fetchFunnels = async ({
    pageParam,
  }: {
    pageParam: string | undefined;
  }): Promise<FunnelListResponse> => {
    const { filterBy, searchBy, sortBy, page, pageSize } = queryParams;

    return await CrmFunnelsService.getFunnels(
      filterBy,
      searchBy,
      sortBy,
      page,
      pageSize,
      pageParam
    );
  };

  const getNextPageParam = (lastPage: FunnelListResponse) =>
    lastPage.nextPageToken;

  const {
    data,
    error,
    isLoading: loading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['crm/funnels', queryParams],
    queryFn: fetchFunnels,
    getNextPageParam,
    initialPageParam: undefined,
    enabled,
  });

  const funnels = useMemoizedArrayData<Funnel>(
    data?.pages.flatMap((page) => page.items) || []
  );

  return {
    loading,
    error,
    funnels,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};

interface UseFunnelParams {
  id: number;
  enabled?: boolean;
}

interface UseFunnelResponse extends ApiResponse {
  funnel: Funnel | null;
}

export const useFunnel = ({
  id,
  enabled = true,
}: UseFunnelParams): UseFunnelResponse => {
  const fetchFunnel = async (): Promise<Funnel | null> => {
    return await CrmFunnelsService.getFunnel(id);
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['crm/funnels', id],
    queryFn: fetchFunnel,
    enabled: enabled && !!id,
  });

  return {
    funnel: data || null,
    loading,
    error,
    refetch,
  };
};
